import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { HelpOpenSource } from '../../types';

const ARTICLE_PREFIX = 'article:';

// Article opened from a shared link, kept in memory (not the URL) until Help
// closes: the app can remount right after page load, and the new mount reopens
// it. A refresh clears memory, so it never reopens on its own.
let pendingArticleId: string | null = null;

// PRD: ?help=article:{id} opens Help on that article and the param leaves the
// address bar right away.
export const useHelpDeepLink = (
  open: boolean,
  onOpen: (source?: HelpOpenSource) => void,
  onOpenArticle: (id: string, referrer?: string) => void
) => {
  const { pathname, search, hash, state } = useLocation();
  const navigate = useNavigate();
  // Dev StrictMode runs effects twice; open each article once per mount.
  const handledRef = useRef<string | null>(null);
  const wasOpenRef = useRef(open);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const help = params.get('help');
    // Telemetry: only a fresh link counts; a remount restore sends nothing.
    let fresh = false;
    if (help?.startsWith(ARTICLE_PREFIX)) {
      pendingArticleId = help.slice(ARTICLE_PREFIX.length) || null;
      fresh = true;
      params.delete('help');
      const _ = navigate(
        { pathname, search: params.toString() ? `?${params}` : '', hash },
        { replace: true }
      );
    }

    const id = pendingArticleId;
    if (!id || handledRef.current === id) return;
    handledRef.current = id;
    // Insight Chat's "Open in Help" marks its navigation as a hand-off.
    const handOff = state?.helpSource === 'hand-off';
    if (fresh) {
      onOpen(handOff ? 'hand-off' : 'link');
      onOpenArticle(id, handOff ? 'insight' : 'link');
    } else {
      onOpen();
      onOpenArticle(id);
    }
    // Reacts to the URL only; the callbacks are recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Closing Help forgets the link.
  useEffect(() => {
    if (wasOpenRef.current && !open) {
      pendingArticleId = null;
      handledRef.current = null;
    }
    wasOpenRef.current = open;
  }, [open]);
};

export default useHelpDeepLink;
